import React, { useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import { RegionListItem } from '../regions';
import type { Selection } from '../App';

interface Props {
  bundeslaender: RegionListItem[];
  getLandkreise: (bundeslandId: string) => RegionListItem[];
  selection: Selection;
  onSelect: (selection: Selection) => void;
}

// Menü-Wert eindeutig kodieren: "bl:<id>" (ganzes Bundesland) bzw. "lk:<id>".
const blWert = (id: string) => `bl:${id}`;
const lkWert = (id: string) => `lk:${id}`;

/**
 * Auswahl Bundesland → Landkreis als ein einziges Dropdown (§7.1).
 * Je Bundesland ein „(gesamt)"-Eintrag plus dessen Landkreise. Das Menü schließt
 * nach der Wahl automatisch (MUI-Select). Bidirektional mit der Karte gekoppelt:
 * `selection` steuert den angezeigten Wert, eine Wahl meldet über `onSelect`
 * dieselbe Auswahl zurück. Felder erscheinen bewusst nicht im Menü (zu viele) —
 * sie werden über Zoom/Klick erreicht; ein per Karte gewähltes Feld zeigt im
 * Menü seinen Landkreis.
 */
const RegionSidebar: React.FC<Props> = ({ bundeslaender, getLandkreise, selection, onSelect }) => {
  // Landkreis-Listen je Bundesland (alphabetisch, s. regions.ts) + flacher Index
  // für die schnelle Rückauflösung Wert → Auswahlobjekt.
  const { landkreiseJeBl, lkIndex } = useMemo(() => {
    const map: Record<string, RegionListItem[]> = {};
    const index = new Map<string, { lk: RegionListItem; blId: string }>();
    for (const bl of bundeslaender) {
      const liste = getLandkreise(bl.id);
      map[bl.id] = liste;
      for (const lk of liste) index.set(lk.id, { lk, blId: bl.id });
    }
    return { landkreiseJeBl: map, lkIndex: index };
  }, [bundeslaender, getLandkreise]);

  // Aktueller Menü-Wert aus der Auswahl (Feld → sein Landkreis). Ohne explizite
  // Auswahl ist das ganze (erste) Bundesland vorausgewählt — das entspricht der
  // Standard-Übersicht, daher kein Platzhalter nötig.
  const wert = useMemo(() => {
    if (!selection) return bundeslaender.length ? blWert(bundeslaender[0].id) : '';
    if (selection.level === 'bundesland') return blWert(selection.id);
    if (selection.level === 'landkreis') return lkWert(selection.id);
    if (selection.level === 'feld' && selection.parentLandkreisId) {
      return lkWert(selection.parentLandkreisId);
    }
    return bundeslaender.length ? blWert(bundeslaender[0].id) : '';
  }, [selection, bundeslaender]);

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value.startsWith('bl:')) {
      const id = value.slice(3);
      const bl = bundeslaender.find((b) => b.id === id);
      if (!bl) return;
      onSelect({
        level: 'bundesland',
        id: bl.id,
        name: bl.name,
        verteilung: bl.verteilung,
        verteilungHa: bl.verteilungHa,
        sizeHa: bl.flaecheHa,
      });
    } else if (value.startsWith('lk:')) {
      const eintrag = lkIndex.get(value.slice(3));
      if (!eintrag) return;
      const { lk, blId } = eintrag;
      onSelect({
        level: 'landkreis',
        id: lk.id,
        name: lk.name,
        verteilung: lk.verteilung,
        verteilungHa: lk.verteilungHa,
        sizeHa: lk.flaecheHa,
        parentBundeslandId: blId,
      });
    }
  };

  // Bundesländer mit Landkreisen zu MUI-Menüeinträgen abflachen. ListSubheader
  // gruppiert optisch je Bundesland (nur bei >1 Bundesland sinnvoll).
  const mehrereBl = bundeslaender.length > 1;
  const items: React.ReactNode[] = [];
  for (const bl of bundeslaender) {
    if (mehrereBl) {
      items.push(
        <ListSubheader key={`sub:${bl.id}`} className="region-select-subheader">
          {bl.name}
        </ListSubheader>
      );
    }
    items.push(
      <MenuItem key={blWert(bl.id)} value={blWert(bl.id)} className="region-select-bl">
        {bl.name} (gesamt)
      </MenuItem>
    );
    for (const lk of landkreiseJeBl[bl.id] ?? []) {
      items.push(
        <MenuItem key={lkWert(lk.id)} value={lkWert(lk.id)} className="region-select-lk">
          {lk.name}
        </MenuItem>
      );
    }
  }

  return (
    <Box className="region-sidebar" component="nav" aria-label="Region wählen">
      <Typography variant="h2" className="region-sidebar-title">
        Region wählen
      </Typography>

      <FormControl fullWidth size="small" className="region-select-control">
        <InputLabel id="region-select-label">Region</InputLabel>
        <Select
          labelId="region-select-label"
          id="region-select"
          label="Region"
          value={wert}
          onChange={handleChange}
          MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}
        >
          {items}
        </Select>
      </FormControl>

      <Typography className="region-sidebar-hint">
        Tipp: In die Karte zoomen oder auf eine Region klicken, um bis zur
        Feld-Ebene vorzudringen.
      </Typography>
    </Box>
  );
};

export default RegionSidebar;
